import {
  ChangeDetectorRef,
  Component,
  Input,
  output,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { TagsAnalytics, TagsTarget } from '@core/analytics-legacy';
import { AnalyticsMode } from '@core/analytics-types';
import type { Attribute } from '@core/http-types';
import { TranslatePipe } from '@core/i18n-legacy';
import type { PaginationState } from '@core/ngrx-legacy';
import { AttrDataScope, OfferTagsActions, offerTagsSelectors } from '@core/tags-and-sources-data';
import type { AttributesLoadMore } from '@core/user-interface-legacy';
import {
  CREATE_NEW_ITEM_ID,
  DropdownHeaderComponent,
  AttributesComponent,
  LabelComponent,
  IconButtonComponent,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-offer-tags-wrapper',
  templateUrl: './offer-tags-wrapper.component.html',
  styleUrls: ['./offer-tags-wrapper.component.less'],
  providers: [TagsAnalytics],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LabelComponent,
    AttributesComponent,
    DropdownHeaderComponent,
    IconButtonComponent,
    RouterLink,
    TranslatePipe,
  ],
})
export class OfferTagsWrapperComponent {
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly analytics = inject(TagsAnalytics);
  private readonly actions$ = inject(Actions);

  public readonly showLabel = input<boolean>(true);
  public readonly isReadOnly = input<boolean>(undefined!);
  @Input() public tags: {
    names: string[];
    value: Attribute[];
  };

  public canManageAttributes = false;
  public tagsTypeahead: { isLoading: boolean; meta: PaginationState | null; rows: Attribute[] } = {
    isLoading: false,
    meta: null,
    rows: [],
  };

  public addTag = output<{
    name: string;
    id: number;
  }>();
  public removeTag = output<{
    name: string;
    id: number;
  }>();

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.checkAbility();

    const isPending$ = this.store.select(
      offerTagsSelectors.isPending({
        scope: AttrDataScope.Typeahead,
      }),
    );
    const paginatedAttributes$ = this.store.select(
      offerTagsSelectors.paginatedAttributeSelector({
        scope: AttrDataScope.Typeahead,
      }),
    );

    combineLatest([paginatedAttributes$, isPending$])
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(([paginatedAttributes, isPending]) => {
        this.tagsTypeahead = {
          ...paginatedAttributes,
          rows: paginatedAttributes.rows.filter(s => !this.tags.names.includes(s.value)),
          isLoading: isPending,
        };
        this.cdRef.detectChanges();
      });
  }

  public loadMoreTags(params: AttributesLoadMore): void {
    this.store.dispatch(
      OfferTagsActions.fetchPaginatedCollection({
        payload: { params, scope: AttrDataScope.Typeahead, add: true },
      }),
    );
  }

  public saveTag(tag: Attribute): void {
    const localTagNames = [...this.tags.names, tag.value];
    const localTagValues = [...this.tags.value, tag];
    const isCreatingNewTag = !tag.id || tag.id === CREATE_NEW_ITEM_ID;

    this.tags = {
      names: localTagNames,
      value: localTagValues,
    };

    if (isCreatingNewTag) {
      this.store.dispatch(OfferTagsActions.createOfferTag({ payload: tag.value }));
      this.actions$
        .pipe(
          ofType(OfferTagsActions.createOfferTagSuccess),
          take(1),
          tap(res => {
            const { id, name } = res.payload[0];
            this.addTag.emit({ id, name });
          }),
        )
        .subscribe();
    } else {
      this.addTag.emit({ name: tag.value, id: <number>tag.id });
    }
    this.analytics.trackTagAdded(TagsTarget.Job, AnalyticsMode.Single);
    this.cdRef.detectChanges();
  }

  public deleteTag(tag: Attribute): void {
    const localTagValues = this.tags.value.filter(_tag => _tag.value !== tag.value);
    const localTagNames = this.tags.names.filter(tagName => tagName !== tag.value);

    this.tags = {
      names: localTagNames,
      value: localTagValues,
    };

    this.removeTag.emit({ name: tag.value, id: <number>tag.id });
  }

  public tagTypeaheadUpdate(query: string): void {
    const params = {
      query,
      page: 1,
      limit: 100,
    };

    this.store.dispatch(
      OfferTagsActions.fetchPaginatedCollection({
        payload: {
          params,
          scope: AttrDataScope.Typeahead,
        },
      }),
    );
  }

  private checkAbility(): void {
    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(enabledMap => {
        this.canManageAttributes = enabledMap[Ability.ManageAttributes];
        this.cdRef.detectChanges();
      });
  }
}
